import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Card, Field, TextInput, SelectInput, ErrorBox, btnPrimary, StatusBadge, fmtDate } from "@/components/common/Form";
import { LoadingState } from "@/components/common/States";

export function ProfilePage() {
  const { user, setUser } = useStore();
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/profile/me").then((r) => {
      if (cancelled) return;
      const u = r.data.user, p = r.data.profile || {};
      setForm({ first_name: u.first_name, last_name: u.last_name, phone: u.phone, profile_image: u.profile_image,
        date_of_birth: p.date_of_birth || "", gender: p.gender || "", preferred_language: p.preferred_language || "en" });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!form) return <LoadingState />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const r = await api.put("/profile/me", { ...form, date_of_birth: form.date_of_birth || null });
      setUser(r.data.user); toast.success("Profile updated");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-6">
      <Card title="Personal information" subtitle="This is how you appear across Shri Vrindavan Shop." testId="profile-card">
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><ErrorBox message={error} testId="profile-error" /></div>
          <Field label="First name *"><TextInput testId="profile-first-name" required value={form.first_name} onChange={set("first_name")} /></Field>
          <Field label="Last name"><TextInput testId="profile-last-name" value={form.last_name} onChange={set("last_name")} /></Field>
          <Field label="Phone" hint="Changing your phone will require re-verification."><TextInput testId="profile-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" /></Field>
          <Field label="Date of birth"><TextInput testId="profile-dob" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} /></Field>
          <Field label="Gender"><SelectInput testId="profile-gender" value={form.gender} onChange={set("gender")} options={[["", "Prefer not to say"], ["female", "Female"], ["male", "Male"], ["other", "Other"]]} /></Field>
          <Field label="Preferred language"><SelectInput testId="profile-language" value={form.preferred_language} onChange={set("preferred_language")} options={[["en", "English"], ["hi", "Hindi"]]} /></Field>
          <Field label="Profile image URL" className="sm:col-span-2"><TextInput testId="profile-image" value={form.profile_image} onChange={set("profile_image")} placeholder="https://..." /></Field>
          <div className="sm:col-span-2"><button disabled={saving} data-testid="profile-save" className={btnPrimary}>{saving ? "Saving..." : "Save changes"}</button></div>
        </form>
      </Card>
      <Card title="Account" testId="account-meta-card">
        <dl className="grid sm:grid-cols-2 gap-3 text-sm">
          <div><dt className="text-stone-500">Email</dt><dd className="font-medium">{user.email} <StatusBadge value={user.email_verified ? "VERIFIED" : "NOT_SUBMITTED"} testId="profile-email-status" /></dd></div>
          <div><dt className="text-stone-500">Phone</dt><dd className="font-medium">{user.phone || "—"} {user.phone && <StatusBadge value={user.phone_verified ? "VERIFIED" : "NOT_SUBMITTED"} />}</dd></div>
          <div><dt className="text-stone-500">Member since</dt><dd className="font-medium">{fmtDate(user.created_at)}</dd></div>
          <div><dt className="text-stone-500">Account type</dt><dd className="font-medium capitalize">{user.role.replace("_", " ")} <StatusBadge value={user.status} /></dd></div>
        </dl>
      </Card>
    </div>
  );
}

export function BusinessPage() {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const keys = ["business_name", "business_type", "gst_number", "business_email", "business_phone", "contact_person", "business_address", "billing_address", "shipping_address", "tax_information"];
  useEffect(() => {
    let cancelled = false;
    api.get("/profile/me").then((r) => {
      if (cancelled) return;
      const b = r.data.business || {};
      setForm(Object.fromEntries(keys.map((k) => [k, b[k] || ""])));
      setStatus(b.verification_status || "NOT_SUBMITTED");
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!form) return <LoadingState />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try { await api.put("/profile/business", { ...form, business_email: form.business_email || null }); toast.success("Business profile saved"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const labels = { business_name: "Business name *", business_type: "Business type", gst_number: "GSTIN", business_email: "Business email", business_phone: "Business phone", contact_person: "Contact person", business_address: "Business address", billing_address: "Billing address", shipping_address: "Shipping address", tax_information: "Tax information" };
  return (
    <Card title="Business profile" subtitle="Used for B2B / wholesale purchasing and GST invoicing." testId="business-card"
      action={<StatusBadge value={status} testId="business-status" />}>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><ErrorBox message={error} testId="business-error" /></div>
        {keys.map((k) => (
          <Field key={k} label={labels[k]} className={k.includes("address") || k === "tax_information" ? "sm:col-span-2" : ""}>
            <TextInput testId={`business-${k}`} required={k === "business_name"} value={form[k]} onChange={set(k)} />
          </Field>
        ))}
        <div className="sm:col-span-2"><button disabled={saving} data-testid="business-save" className={btnPrimary}>{saving ? "Saving..." : "Save business profile"}</button></div>
      </form>
    </Card>
  );
}
